import { useMemo } from "react";
import themeData from "./theme.json";
import { getThemeName } from "src/config/themeConfig";

const FALLBACK_THEME = "Kural";
const OPACITY_FLOOR = 0.25;

// Last-resort categorical palette (Tableau 10) used only when theme.json defines no
// `chart-colors` for the active theme OR the fallback theme — e.g. the KCC lift ships
// a theme.json without chart palettes. Keeps getChartPalette() from returning
// undefined (which crashed chart callers). No-op in the full app.
const DEFAULT_CHART_COLORS = ["#76C2AC", "#4B83DC", "#9681E2", "#9939F3", "#EA39F3", "#2EB8BA", "#809C41", "#65D9FF", "#714EAD", "#65249C", "#862778", "#C089E7", "#1F7197", "#56B0DA"];

type ThemeColorMap = Record<string, string | boolean | string[]>;

const getThemeColors = (themeName: string): ThemeColorMap => (themeData.colors as Record<string, ThemeColorMap>)[themeName];

// The `chart-colors` array lives only in theme.json (not on :root, since CSS custom
// properties can't hold arrays) — themes that don't define one fall back to Kural's.
const getBasePalette = (): string[] => {
	const active = getThemeColors(getThemeName())?.["chart-colors"];
	if (Array.isArray(active) && active.length > 0) return active;
	const fallback = getThemeColors(FALLBACK_THEME)?.["chart-colors"];
	if (Array.isArray(fallback) && fallback.length > 0) return fallback as string[];
	return DEFAULT_CHART_COLORS;
};

/**
 * The active theme's base `chart-colors` array (from theme.json), for callers that
 * index into it directly by a stable slot rather than requesting an exact series
 * count — e.g. persona accent colors keyed by `paletteIndex`. Prefer
 * `useChartPalette(n)` / `getChartPalette(n)` for actual chart series.
 */
export const getChartColorPalette = (): string[] => getBasePalette();

const hexToRgba = (hex: string, alpha: number): string => {
	const h = hex.replace("#", "");
	const r = parseInt(h.substring(0, 2), 16);
	const g = parseInt(h.substring(2, 4), 16);
	const b = parseInt(h.substring(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha * 100) / 100})`;
};

/**
 * Pure, non-hook palette builder — safe to call from plain option-builder
 * functions (e.g. ECharts config factories) that aren't React components.
 *
 * Returns the first `n` base chart-colors at full opacity. If `n` exceeds the
 * base palette length, cycles through the base colors again at progressively
 * lower opacity, spaced evenly from 1.0 down to a floor of 0.25, so adjacent
 * series stay visually distinguishable regardless of chart density.
 */
export const getChartPalette = (n: number): string[] => {
	const base = getBasePalette();
	if (n <= 0) return [];
	if (n <= base.length) return base.slice(0, n);

	const tiers = Math.ceil(n / base.length);
	const colors: string[] = [];
	for (let tier = 0; tier < tiers && colors.length < n; tier++) {
		const opacity = tiers === 1 ? 1 : 1 - (tier * (1 - OPACITY_FLOOR)) / (tiers - 1);
		for (let i = 0; i < base.length && colors.length < n; i++) {
			colors.push(tier === 0 ? base[i] : hexToRgba(base[i], opacity));
		}
	}
	return colors;
};

/**
 * Default chart-series palette for FE-driven charts (lines, bars, donuts).
 * `n` must be the exact series count — an inflated `n` produces washed-out
 * colors unnecessarily, a too-small `n` causes out-of-range index reads.
 * Backend-provided series colors (e.g. `chart_config.ser_color`) remain
 * authoritative; this is only the fallback for charts the BE doesn't cover.
 */
export function useChartPalette(n: number): string[];
export function useChartPalette(n: number, seriesNames: string[]): Record<string, string>;
export function useChartPalette(n: number, seriesNames?: string[]): string[] | Record<string, string> {
	return useMemo(() => {
		const palette = getChartPalette(n);
		if (!seriesNames) return palette;
		return seriesNames.reduce<Record<string, string>>((map, name, i) => {
			map[name] = palette[i];
			return map;
		}, {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [n, seriesNames]);
}
